import { useEffect, useMemo, useState } from 'react';
import { createClient, listClients } from '../lib/api';
import type { Client } from '../types';
import { GBIBtn, T, sharedStyles } from './Shared';

interface NewClientModalProps {
  onClose: () => void;
  onCreated: (client: Client) => void;
  // When opened from inside a business (Settings → add location), prefill the
  // parent so the new client is created as a branch of it.
  defaultParent?: { id: string; name: string };
}

// Extract place_id from any Google Maps URL pattern we accept:
//   • https://www.google.com/maps/place/.../data=!3m1!4b1!... — no place_id
//   • https://maps.app.goo.gl/<short> — needs server-side resolution (skipped here)
//   • plain ChIJ... pasted into the input
// Returns null if nothing useful found; user can fall back to typing it manually.
function extractPlaceIdFromInput(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  // Bare ChIJ format.
  if (/^ChIJ[\w-]{10,}$/.test(v)) return v;
  // ?place_id=ChIJ... query param.
  const qpMatch = v.match(/[?&]place_id=([\w-]+)/i);
  if (qpMatch) return qpMatch[1];
  // /place/.../ChIJ... or any embedded ChIJ token.
  const inline = v.match(/(ChIJ[\w-]{10,})/);
  if (inline) return inline[1];
  return null;
}

// Extract @lat,lng,zoom from a Google Maps URL. Returns null if not present.
function extractLatLngFromInput(raw: string): { lat: number; lng: number } | null {
  const m = raw.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function NewClientModal({ onClose, onCreated, defaultParent }: NewClientModalProps) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [mapsInput, setMapsInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Multi-location: optionally attach this as a branch of an existing business.
  const [asLocation, setAsLocation] = useState<boolean>(!!defaultParent);
  const [parentId, setParentId] = useState<string>(defaultParent?.id ?? '');
  const [locationLabel, setLocationLabel] = useState('');
  const [heads, setHeads] = useState<Client[] | null>(null);
  const [parentQuery, setParentQuery] = useState(defaultParent?.name ?? '');

  // Load candidate parent businesses (heads only) the first time the toggle is on.
  useEffect(() => {
    if (!asLocation || heads !== null) return;
    listClients()
      .then(list => setHeads(list.filter(c => !c.parentClientId)))
      .catch(() => setHeads([]));
  }, [asLocation, heads]);

  const parentMatches = useMemo(() => {
    if (!heads) return [];
    const s = parentQuery.trim().toLowerCase();
    const pool = heads.filter(h => h.id !== parentId);
    if (!s) return pool.slice(0, 8);
    return pool.filter(h => h.name.toLowerCase().includes(s) || h.location.toLowerCase().includes(s)).slice(0, 8);
  }, [heads, parentQuery, parentId]);

  const placeId = extractPlaceIdFromInput(mapsInput);
  const latLng = extractLatLngFromInput(mapsInput);

  const canSubmit = name.trim().length > 0 && location.trim().length > 0 && !busy &&
    (!asLocation || parentId.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const client = await createClient({
        name: name.trim(),
        location: location.trim(),
        category: category.trim() || undefined,
        gbpPlaceId: placeId ?? undefined,
        lat: latLng?.lat,
        lng: latLng?.lng,
        parentClientId: asLocation && parentId ? parentId : undefined,
        locationLabel: asLocation ? (locationLabel.trim() || location.trim()) : undefined,
      });
      onCreated(client);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={busy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 520, maxWidth: '92vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 20, fontWeight: 700, color: T.fgPrimary, marginBottom: 6 }}>
          New Client
        </div>
        <div style={{ fontSize: 12, color: T.fgMuted, marginBottom: 20, lineHeight: 1.5 }}>
          Paste the Google Maps URL or Place ID to auto-link this client. You can also fill
          it in later via Settings.
        </div>

        <Field label="Business Name *" hint="As shown on Google Maps.">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kyuten Robatayaki Japanese Izakaya"
            style={inputStyle} disabled={busy} />
        </Field>

        <Field label="Location *" hint="City / area as seen on the cards.">
          <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Johor Bahru"
            style={inputStyle} disabled={busy} />
        </Field>

        <Field label="Category" hint="e.g. Japanese Restaurant, Marketing Agency.">
          <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Japanese Restaurant"
            style={inputStyle} disabled={busy} />
        </Field>

        {/* Multi-location */}
        <div style={{
          border: `1px solid ${asLocation ? T.cyan : T.border}`,
          borderRadius: 8, padding: '12px 14px', marginBottom: 14,
          background: asLocation ? 'rgba(18,181,196,0.05)' : T.bgSubtle,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>
            <input
              type="checkbox"
              checked={asLocation}
              onChange={e => setAsLocation(e.target.checked)}
              disabled={busy || !!defaultParent}
            />
            This is another location of an existing client
          </label>
          <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 4, lineHeight: 1.5 }}>
            Use this when the same business has multiple branches (different Google
            place_id each). They'll share one client card with a location switcher.
          </div>

          {asLocation && (
            <div style={{ marginTop: 12 }}>
              {defaultParent ? (
                <div style={{
                  fontSize: 12, color: T.fgPrimary, padding: '8px 10px',
                  background: '#fff', border: `1px solid ${T.cyan}`, borderRadius: 6,
                }}>
                  Parent business: <strong>{defaultParent.name}</strong>
                </div>
              ) : (
                <>
                  <input
                    value={parentQuery}
                    onChange={e => { setParentQuery(e.target.value); setParentId(''); }}
                    placeholder="Search the parent business by name…"
                    style={{ ...inputStyle, marginBottom: 6 }}
                    disabled={busy}
                  />
                  {heads === null && <div style={{ fontSize: 11, color: T.fgMuted }}>Loading businesses…</div>}
                  {heads !== null && (
                    <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {parentMatches.length === 0 && (
                        <div style={{ fontSize: 11, color: T.fgMuted, fontStyle: 'italic', padding: 6 }}>No matching business.</div>
                      )}
                      {parentMatches.map(h => {
                        const on = parentId === h.id;
                        return (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => { setParentId(h.id); setParentQuery(h.name); }}
                            style={{
                              textAlign: 'left', padding: '8px 10px', borderRadius: 6,
                              border: `1px solid ${on ? T.cyan : T.border}`,
                              background: on ? 'rgba(18,181,196,0.08)' : '#fff',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.fgPrimary }}>
                              {on ? '● ' : ''}{h.name}
                            </div>
                            <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 1 }}>📍 {h.location}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              <div style={{ marginTop: 10 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: T.fgSecondary, marginBottom: 6,
                }}>Branch label</div>
                <input
                  value={locationLabel}
                  onChange={e => setLocationLabel(e.target.value)}
                  placeholder="e.g. Pelangi (defaults to the Location above)"
                  style={inputStyle}
                  disabled={busy}
                />
              </div>
            </div>
          )}
        </div>

        <Field
          label="Google Maps URL or Place ID"
          hint="Paste maps.app.goo.gl link, google.com/maps URL, or a bare ChIJ... — we'll extract place_id and coordinates automatically. Leave blank to add later."
        >
          <textarea value={mapsInput} onChange={e => setMapsInput(e.target.value)}
            placeholder="https://maps.app.goo.gl/xyz   or   ChIJhe4f4Xpt2jERwnl309lmHQQ"
            rows={2}
            style={{ ...inputStyle, fontFamily: sharedStyles.fontMono, fontSize: 11, resize: 'vertical' }}
            disabled={busy} />
          {mapsInput.trim() && (
            <div style={{ marginTop: 6, fontSize: 11, color: T.fgMuted, fontFamily: sharedStyles.fontMono }}>
              {placeId ? <>📍 place_id: <span style={{ color: T.cyan }}>{placeId}</span></> : <span style={{ color: T.amber }}>⚠ No ChIJ place_id detected. The client will be created without one — you can add it via Settings, or Sync will try to resolve it via Places search.</span>}
              {latLng && <> · 🌐 {latLng.lat.toFixed(6)}, {latLng.lng.toFixed(6)}</>}
            </div>
          )}
        </Field>

        {error && (
          <div style={{
            marginTop: 14, padding: '10px 12px',
            background: 'rgba(232,80,26,0.08)', border: '1px solid #E8501A',
            borderRadius: 6, fontSize: 12, color: '#C03B0F',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
          <button onClick={onClose} disabled={busy} style={{
            background: 'transparent', border: `1px solid ${T.border}`,
            borderRadius: 6, padding: '9px 16px', cursor: busy ? 'wait' : 'pointer',
            fontSize: 13, color: T.fgSecondary,
          }}>Cancel</button>
          <GBIBtn variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create Client'}
          </GBIBtn>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', fontSize: 13,
  fontFamily: sharedStyles.fontBody, color: T.fgPrimary,
  background: '#fff', border: `1px solid ${T.border}`, borderRadius: 6,
  outline: 'none', boxSizing: 'border-box',
};

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: T.fgSecondary, marginBottom: 6,
      }}>{label}</div>
      {children}
      {hint && (
        <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 4, lineHeight: 1.5 }}>{hint}</div>
      )}
    </div>
  );
}
